import React, { useEffect, useState, useRef, Component } from 'react';
import { ShareIcon, TwitterIcon, FacebookIcon, CopyIcon, CheckIcon } from 'lucide-react';
import { DrawingCanvas } from './DrawingCanvas';
import { useSound } from './SoundEffects';
export const Hero = ({
  onCursorChange
}) => {
  const [mousePosition, setMousePosition] = useState({
    x: 0,
    y: 0
  });
  const containerRef = useRef(null);
  const imageRef = useRef(null);
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const {
    playSound
  } = useSound();
  // Handle mouse movement for parallax effects
  const handleMouseMove = e => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left - rect.width / 2) / 20;
    const y = (e.clientY - rect.top - rect.height / 2) / 20;
    setMousePosition({
      x,
      y
    });
    // Update image tilt
    if (imageRef.current) {
      imageRef.current.style.transform = `perspective(1000px) rotateX(${-y * 0.5}deg) rotateY(${x * 0.5}deg) scale3d(1.02, 1.02, 1.02)`;
    }
  };
  // Share functions
  const shareToTwitter = () => {
    playSound('click');
    const text = "Exploring Cheryl Qian's Biological Distortion - where flesh meets technology in a beautiful cyberpunk experience.";
    const url = window.location.href;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
  };
  const shareToFacebook = () => {
    playSound('click');
    const url = window.location.href;
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
  };
  const copyPageLink = () => {
    playSound('success');
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }, err => {
      console.error('Could not copy text: ', err);
    });
  };
  const toggleShareOptions = () => {
    playSound('click');
    setShowShareOptions(!showShareOptions);
  };
  useEffect(() => {
    // Add event listener for mouse movement
    if (containerRef.current) {
      containerRef.current.addEventListener('mousemove', handleMouseMove);
    }
    return () => {
      if (containerRef.current) {
        containerRef.current.removeEventListener('mousemove', handleMouseMove);
      }
    };
  }, []);
  return <section ref={containerRef} className="w-full min-h-screen flex items-center justify-center pt-20 relative overflow-hidden" data-id="element-970">
      <style jsx global data-id="element-971">{`
        .hero-image {
          transition: transform 0.3s ease-out;
          transform-style: preserve-3d;
          will-change: transform;
          box-shadow: 0 0 30px rgba(111, 248, 240, 0.3);
        }
        .hero-title {
          opacity: 0;
          transform: translateY(20px);
          animation: fade-in-up 0.8s forwards 0.3s;
          text-shadow: 0 0 20px rgba(111, 248, 240, 0.4);
        }
        .hero-subtitle {
          opacity: 0;
          transform: translateY(20px);
          animation: fade-in-up 0.8s forwards 0.6s;
        }
        .hero-description {
          opacity: 0;
          transform: translateY(20px);
          animation: fade-in-up 0.8s forwards 0.9s;
        }
        .hero-buttons {
          opacity: 0;
          transform: translateY(20px);
          animation: fade-in-up 0.8s forwards 1.2s;
        }
        .hero-image-container {
          opacity: 0;
          animation: fade-in 1.2s forwards 1s;
        }
        .ethereal-glow {
          position: absolute;
          width: 150%;
          height: 150%;
          top: -25%;
          left: -25%;
          background: radial-gradient(
            circle,
            rgba(111, 248, 240, 0.15) 0%,
            rgba(111, 248, 240, 0) 70%
          );
          pointer-events: none;
          opacity: 0;
          transition: opacity 0.5s ease;
        }
        .hero-button:hover .ethereal-glow {
          opacity: 1;
        }
        @keyframes pulse-glow {
          0%,
          100% {
            box-shadow: 0 0 15px rgba(111, 248, 240, 0.3);
          }
          50% {
            box-shadow: 0 0 25px rgba(111, 248, 240, 0.5);
          }
        }
        .circuit-lines {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 1;
          opacity: 0.15;
          pointer-events: none;
        }
        .circuit-line {
          position: absolute;
          background: linear-gradient(
            90deg,
            rgba(111, 248, 240, 0) 0%,
            rgba(111, 248, 240, 1) 50%,
            rgba(111, 248, 240, 0) 100%
          );
          height: 1px;
        }
        .circuit-line-1 {
          top: 20%;
          left: 0;
          right: 0;
          animation: circuit-line-anim 15s infinite linear;
        }
        .circuit-line-2 {
          top: 60%;
          left: 0;
          right: 0;
          animation: circuit-line-anim 20s infinite linear;
          animation-delay: 5s;
        }
        .circuit-line-3 {
          top: 80%;
          left: 0;
          right: 0;
          animation: circuit-line-anim 12s infinite linear;
          animation-delay: 2s;
        }
        @keyframes circuit-line-anim {
          0% {
            opacity: 0;
            background-position: -100% 0;
          }
          50% {
            opacity: 1;
          }
          100% {
            opacity: 0;
            background-position: 200% 0;
          }
        }
        .share-options {
          position: absolute;
          top: 100%;
          right: 0;
          background: rgba(3, 5, 8, 0.95);
          border: 1px solid rgba(111, 248, 240, 0.3);
          border-radius: 8px;
          padding: 10px;
          margin-top: 8px;
          z-index: 50;
          box-shadow: 0 0 30px rgba(111, 248, 240, 0.2);
          backdrop-filter: blur(10px);
          width: 180px;
        }
        .share-button {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          width: 100%;
          border-radius: 4px;
          background: transparent;
          color: #a0c0e0;
          border: none;
          transition: all 0.3s ease;
          text-align: left;
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.75rem;
        }
        .share-button:hover {
          background: rgba(111, 248, 240, 0.1);
          color: #6ff8f0;
        }
        .share-button-twitter {
          color: #1da1f2;
        }
        .share-button-facebook {
          color: #4267b2;
        }
        .share-button-copy {
          color: #a0c0e0;
        }
        .success-message {
          position: fixed;
          bottom: 20px;
          left: 50%;
          transform: translateX(-50%);
          background: rgba(111, 248, 240, 0.2);
          border: 1px solid rgba(111, 248, 240, 0.5);
          border-radius: 4px;
          padding: 8px 16px;
          color: #6ff8f0;
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.75rem;
          display: flex;
          align-items: center;
          gap: 8px;
          animation: fadeInOut 2s forwards;
          z-index: 100;
        }
        @keyframes fadeInOut {
          0% {
            opacity: 0;
            transform: translate(-50%, 20px);
          }
          20% {
            opacity: 1;
            transform: translate(-50%, 0);
          }
          80% {
            opacity: 1;
            transform: translate(-50%, 0);
          }
          100% {
            opacity: 0;
            transform: translate(-50%, -20px);
          }
        }
      `}</style>

      {/* Circuit lines */}
      <div className="circuit-lines" data-id="element-972">
        <div className="circuit-line circuit-line-1" data-id="element-973"></div>
        <div className="circuit-line circuit-line-2" data-id="element-974"></div>
        <div className="circuit-line circuit-line-3" data-id="element-975"></div>
      </div>

      <div className="container mx-auto px-6 py-16 md:py-28 relative z-10" data-id="element-976">
        <div className="flex flex-col md:flex-row items-center justify-between gap-10" data-id="element-977">
          <div className="md:w-1/2 text-center md:text-left" data-id="element-978">
            <h1 className="hero-title text-4xl md:text-6xl lg:text-7xl font-bold mb-3 leading-tight tracking-tighter font-heading" data-id="element-979">
              <span className="text-gradient" data-id="element-980">CHERYL QIAN</span>
            </h1>
            <h2 className="hero-subtitle text-xl md:text-2xl lg:text-3xl text-[#a0c0e0] mt-1 mb-4 uppercase tracking-wider font-mono" data-id="element-981">
              BIOLOGICAL DISTORTION
            </h2>
            <div className="hero-description text-lg md:text-xl max-w-xl mx-auto md:mx-0 leading-tight text-[#a0c0e0] mb-6 font-mono" data-id="element-982">
              <p className="mb-4" data-id="element-983">Raw beauty at the flesh-tech intersection.</p>
            </div>
            <div className="hero-buttons flex flex-wrap justify-center md:justify-start gap-4" data-id="element-984">
              <a href="#portfolio" className="hero-button px-8 py-3 glass border border-[#6ff8f0]/50 text-[#6ff8f0] 
                  hover:bg-[#6ff8f0]/10 transition-all duration-300 
                  uppercase tracking-wider font-mono relative overflow-hidden group" onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} onClick={() => playSound('click')} style={{
              animation: 'pulse-glow 3s infinite'
            }} data-id="element-985">
                <div className="ethereal-glow" data-id="element-986"></div>
                <span className="relative z-10" data-id="element-987">WITNESS</span>
                <span className="absolute bottom-0 left-0 h-[2px] w-0 bg-[#6ff8f0] transition-all duration-300 group-hover:w-full" data-id="element-988"></span>
              </a>
              <a href="#contact" className="hero-button px-8 py-3 glass border border-[#a0c0e0]/30 text-[#a0c0e0] 
                  hover:bg-[#a0c0e0]/10 transition-all duration-300 
                  uppercase tracking-wider font-mono relative overflow-hidden group" onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} onClick={() => playSound('click')} data-id="element-989">
                <div className="ethereal-glow" data-id="element-990"></div>
                <span className="relative z-10" data-id="element-991">CONTACT</span>
                <span className="absolute bottom-0 left-0 h-[2px] w-0 bg-[#a0c0e0] transition-all duration-300 group-hover:w-full" data-id="element-992"></span>
              </a>
              {/* Share button */}
              <div className="relative" data-id="element-993">
                <button className="hero-button px-8 py-3 glass border border-[#a0c0e0]/30 text-[#a0c0e0] 
                    hover:bg-[#a0c0e0]/10 transition-all duration-300 
                    uppercase tracking-wider font-mono relative overflow-hidden group" onClick={toggleShareOptions} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-994">
                  <div className="ethereal-glow" data-id="element-995"></div>
                  <span className="relative z-10 flex items-center gap-2" data-id="element-996">
                    <ShareIcon size={16} data-id="element-997" />
                    SHARE
                  </span>
                  <span className="absolute bottom-0 left-0 h-[2px] w-0 bg-[#a0c0e0] transition-all duration-300 group-hover:w-full" data-id="element-998"></span>
                </button>
                {/* Share Options Popup */}
                {showShareOptions && <div className="share-options" data-id="element-999">
                    <button className="share-button share-button-twitter" onClick={shareToTwitter} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-1000">
                      <TwitterIcon size={16} data-id="element-1001" />
                      Share on Twitter
                    </button>
                    <button className="share-button share-button-facebook" onClick={shareToFacebook} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-1002">
                      <FacebookIcon size={16} data-id="element-1003" />
                      Share on Facebook
                    </button>
                    <button className="share-button share-button-copy" onClick={copyPageLink} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-1004">
                      <CopyIcon size={16} data-id="element-1005" />
                      Copy Link
                    </button>
                  </div>}
              </div>
            </div>
            {/* Orchid Image (moved from right column) */}
            <div className="mt-8 glass border border-[#6ff8f0]/30 p-4 rounded-lg" data-id="element-1006">
              <h3 className="text-[#6ff8f0] font-mono text-sm mb-2 uppercase text-center" data-id="element-1007">
                Synaptic Garden: Orchid Suite
              </h3>
              <div ref={imageRef} className="hero-image overflow-hidden relative group" onMouseEnter={() => onCursorChange('image')} onMouseLeave={() => onCursorChange('default')} data-id="element-1008">
                {/* Image with effects */}
                <div className="relative overflow-hidden rounded-lg interface-border" data-id="element-1009">
                  {/* Orchid Image Gallery */}
                  <a href="https://orchidrepublic.com/collections/orchid-plant-arrangements/products/almost-paradise" target="_blank" rel="noopener noreferrer" className="block w-full h-full" onClick={() => playSound('click')} data-id="element-1010">
                    <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/sb6h6Ejben2GShAZPsdmoM/orchid_1.jpg" alt="Biological Distortion - Deep Purple Orchid Specimen" className="w-full h-auto transition-all duration-700 filter contrast-150 brightness-100 group-hover:brightness-110" data-id="element-1011" />
                  </a>
                  {/* Overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#000000] via-transparent to-transparent opacity-40" data-id="element-1012"></div>
                  {/* Scanning effect */}
                  <div className="absolute inset-0 overflow-hidden" data-id="element-1013">
                    <div className="scan-line" data-id="element-1014"></div>
                  </div>
                  {/* Animated scan line effect */}
                  <div className="absolute inset-0 bg-[linear-gradient(transparent_50%,rgba(16,22,36,0.2)_50%)] bg-[size:100%_4px] opacity-0 group-hover:opacity-30 transition-opacity duration-500 animate-[scanlines_8s_linear_infinite]" data-id="element-1015"></div>
                  {/* Interactive glow on hover */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-30 bg-gradient-to-r from-[#6ff8f0] to-[#e64cff] mix-blend-overlay transition-opacity duration-500" data-id="element-1016"></div>
                </div>
                {/* Border effects */}
                <div className="absolute -inset-0.5 bg-gradient-to-br from-[#6ff8f0]/50 to-[#e64cff]/50 opacity-0 group-hover:opacity-100 blur-sm transition-opacity duration-500 -z-10" data-id="element-1017"></div>
                {/* Corner accents */}
                <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-[#6ff8f0]/70 opacity-0 group-hover:opacity-100 transition-all duration-500" data-id="element-1018"></div>
                <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-[#e64cff]/70 opacity-0 group-hover:opacity-100 transition-all duration-500" data-id="element-1019"></div>
                {/* Ethereal glow effect */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-1020">
                  <div className="absolute -inset-10 bg-[#6ff8f0] opacity-10 blur-3xl" data-id="element-1021"></div>
                </div>
              </div>
            </div>
          </div>
          {/* Drawing Canvas (moved from bottom of left column) */}
          <div className="md:w-1/2 hero-image-container" data-id="element-1022">
            <div className="glass border border-[#6ff8f0]/30 p-4 rounded-lg" data-id="element-1023">
              <h3 className="text-[#6ff8f0] font-mono text-sm mb-2 uppercase text-center" data-id="element-1024">
                Neural Bloom: Orchid Protocol
              </h3>
              <DrawingCanvas onCursorChange={onCursorChange} data-id="element-1025" />
            </div>
          </div>
        </div>
      </div>
      {copySuccess && <div className="success-message" data-id="element-1026">
          <CheckIcon size={14} data-id="element-1027" />
          Link copied to clipboard
        </div>}
    </section>;
};